import * as React from 'react'
import type { Language } from 'codegen/types'
import {
  getBrowserPreferredLanguages,
  resolveSelectableStudioLanguage,
} from 'cf-i18n/browser-language'
import { usePrefetchLanguage } from '@repo/studio-i18n-hooks/use-prefetch-language'

/**
 * How long a pointer has to rest on a row before it counts as intent. Reaching
 * for one language drags the cursor across several others, and each of those is
 * a whole bundle we would rather not fetch on the way past.
 */
const HOVER_INTENT_MS = 120

function hasFinePointerDevice(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false
  }

  return window.matchMedia('(pointer: fine)').matches
}

/**
 * Decides which languages the picker fetches ahead of a switch.
 *
 * Two guesses, both only worth making while the picker is open, because that is
 * when a switch is imminent:
 *
 * - the language the **browser** prefers, when the reader is not already using
 *   it. Someone on `/fr` whose browser asks for Spanish has told us what they
 *   are most likely to pick.
 * - the language a pointer or focus **rests on**, which is a much stronger
 *   signal and covers the reader who wants none of the above.
 *
 * Restricted to fine-pointer devices: phones and tablets are the most likely to
 * be on a metered or slow connection, where speculative chunks compete with the
 * page the reader is actually looking at. Each language is fetched at most once
 * per session, so sweeping back over a row costs nothing.
 */
export function useLanguagePrefetch({
  open,
  currentLanguage,
}: {
  open: boolean
  currentLanguage: Language
}) {
  const prefetchLanguage = usePrefetchLanguage()
  const prefetchedLanguagesRef = React.useRef(new Set<Language>())
  const hoverIntentRef = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const prefetch = React.useCallback(
    (language: Language) => {
      if (
        language === currentLanguage ||
        prefetchedLanguagesRef.current.has(language) ||
        !hasFinePointerDevice()
      ) {
        return
      }

      prefetchedLanguagesRef.current.add(language)
      prefetchLanguage(language)
    },
    [currentLanguage, prefetchLanguage],
  )

  const cancelHoverPrefetch = React.useCallback(() => {
    clearTimeout(hoverIntentRef.current)
  }, [])

  const prefetchOnHoverIntent = React.useCallback(
    (language: Language) => {
      // Cancels first, so moving to another row supersedes the row behind it.
      cancelHoverPrefetch()
      hoverIntentRef.current = setTimeout(() => prefetch(language), HOVER_INTENT_MS)
    },
    [cancelHoverPrefetch, prefetch],
  )

  React.useEffect(() => {
    if (!open) {
      return
    }

    const preferredLanguage = resolveSelectableStudioLanguage(getBrowserPreferredLanguages())

    if (preferredLanguage) {
      prefetch(preferredLanguage)
    }
  }, [open, prefetch])

  // A row the pointer left as the panel closed must not fetch after the fact.
  React.useEffect(() => cancelHoverPrefetch, [cancelHoverPrefetch, open])

  return { prefetchOnHoverIntent, cancelHoverPrefetch }
}
